import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Globe,
  Map as MapIcon,
  Plus,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Tab, TabList, TabPanel, Tabs, useTabs } from '../../components/ui/Tabs';
import { Fail2banReactionCard } from '../../components/ui/Fail2banReactionToggle';
import { useSearchParams } from 'react-router-dom';
import { GeoMap } from '../../components/geo/GeoMap';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Types ────────────────────────────────────────────────────────────────────

interface IpEntry {
  id: string;
  list_type: 'blacklist' | 'whitelist';
  ip: string;
  cidr: number | null;
  source: string;
  reason: string | null;
  expires_at: string | null;
  node_id: string | null;
  created_at: string;
}

interface SecurityEvent {
  event_uuid: string;
  ts: string;
  source_ip: string;
  kind: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  subject: string | null;
  reason: string | null;
  source_module: string;
  source_node: string;
  details: Record<string, unknown>;
  persistent: boolean;
}

interface AggregatedRow {
  source_ip: string;
  cnt: number;
  first_ts: string;
  last_ts: string;
  kinds: string[];
}

// ── MCP helper ───────────────────────────────────────────────────────────────

async function callMcp<T = unknown>(name: string, args: Record<string, unknown>): Promise<T> {
  return api.mcp<T>(name, args);
}

// ── Components ───────────────────────────────────────────────────────────────

/** Client-side private IP check (aligned with geo.ip_classify / RFC1918). */
function isPrivateIpClient(ip: string): boolean {
  const raw = ip.trim().toLowerCase();
  if (!raw) return false;
  if (raw.includes(':')) {
    if (raw === '::1' || raw.startsWith('fc') || raw.startsWith('fd') || raw.startsWith('fe80:')) {
      return true;
    }
    if (raw.startsWith('::ffff:')) return isPrivateIpClient(raw.slice(7));
    return false;
  }
  const m = raw.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (a === 10 || a === 127 || a === 0) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

function IpListPanel({ listType }: { listType: 'blacklist' | 'whitelist' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newExpiresHours, setNewExpiresHours] = useState('0');
  const [scope, setScope] = useState<'all' | 'private' | 'public'>('all');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['self-defence-ip-list', listType],
    queryFn: () =>
      callMcp<{ ok: boolean; entries: IpEntry[] }>('self-defence.ip_list_query', {
        list_type: listType,
      }),
    refetchInterval: 30_000,
  });
  const allEntries = data?.entries ?? [];
  const entries = allEntries.filter((e) => {
    if (scope === 'all') return true;
    const priv = isPrivateIpClient(e.ip);
    return scope === 'private' ? priv : !priv;
  });
  const privateCount = allEntries.filter((e) => isPrivateIpClient(e.ip)).length;
  const publicCount = allEntries.length - privateCount;

  const addMut = useMutation({
    mutationFn: () =>
      callMcp('self-defence.ip_list_add', {
        list_type: listType,
        ip: newIp.trim(),
        reason: newReason.trim() || undefined,
        expires_hours: parseInt(newExpiresHours, 10) || 0,
        source: 'manual',
      }),
    onSuccess: () => {
      toast.success(t('selfDefence.addedToList', { list: listType }));
      setAdding(false);
      setNewIp('');
      setNewReason('');
      setNewExpiresHours('0');
      qc.invalidateQueries({ queryKey: ['self-defence-ip-list', listType] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeMut = useMutation({
    mutationFn: (ip: string) =>
      callMcp('self-defence.ip_list_remove', { list_type: listType, ip }),
    onSuccess: () => {
      toast.success(t('selfDefence.removed'));
      qc.invalidateQueries({ queryKey: ['self-defence-ip-list', listType] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const publishMut = useMutation({
    mutationFn: ({ ip, publish }: { ip: string; publish: boolean }) =>
      callMcp('self-defence.ip_list_publish', { list_type: listType, ip, publish }),
    onSuccess: (_r, vars) => {
      toast.success(vars.publish ? t('selfDefence.publishedToMesh') : t('selfDefence.removedFromMesh'));
      qc.invalidateQueries({ queryKey: ['self-defence-ip-list', listType] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const publishAllMut = useMutation({
    mutationFn: () =>
      callMcp<{ published?: number }>('self-defence.ip_list_publish_all', { list_type: listType }),
    onSuccess: (r) => {
      toast.success(t('selfDefence.publishedAllToMesh', { n: r.published ?? 0 }));
      qc.invalidateQueries({ queryKey: ['self-defence-ip-list', listType] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const Icon = listType === 'blacklist' ? Ban : ShieldCheck;

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={Icon}>
            {listType === 'blacklist' ? t('selfDefence.blacklist') : t('selfDefence.whitelist')}{' '}
            ({entries.length}/{allEntries.length})
          </CardTitle>
          <div className='flex gap-2 flex-wrap items-center'>
            <Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value='all'>{t('selfDefence.scopeAll')} ({allEntries.length})</option>
              <option value='public'>{t('selfDefence.scopePublic')} ({publicCount})</option>
              <option value='private'>{t('selfDefence.scopePrivate')} ({privateCount})</option>
            </Select>
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('selfDefence.refresh')}
            </Button>
            <Button
              variant='secondary'
              size='sm'
              icon={<Globe size={12} />}
              loading={publishAllMut.isPending}
              onClick={() => publishAllMut.mutate()}
              title={t('selfDefence.publishAllTitle')}
            >
              {t('selfDefence.shareAll')}
            </Button>
            <Button variant='primary' size='sm' icon={<Plus size={12} />} onClick={() => setAdding(true)}>
              {t('selfDefence.add')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <div className='text-xs text-gray-600 py-4'>{t('common.loading')}</div>
          : entries.length === 0
            ? <div className='text-xs text-gray-600 py-4 text-center'>{t('selfDefence.noEntries')}</div>
            : (
              <div className='overflow-x-auto'>
                <table className='w-full text-xs'>
                  <thead>
                    <tr className='text-gray-500 border-b border-gray-800'>
                      <th className='text-left py-1 px-2'>IP</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colScope')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colSource')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colReason')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colExpires')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colNode')}</th>
                      <th className='text-right py-1 px-2'>{t('selfDefence.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const isShared = e.source === 'mqtt-peer';
                      const priv = isPrivateIpClient(e.ip);
                      return (
                        <tr key={e.id} className='border-b border-gray-900 hover:bg-gray-900/40'>
                          <td className='py-1 px-2 font-mono text-white'>
                            {e.ip}{e.cidr ? `/${e.cidr}` : ''}
                          </td>
                          <td className='py-1 px-2'>
                            <Badge variant={priv ? 'default' : 'info'}>
                              {priv ? t('selfDefence.scopePrivate') : t('selfDefence.scopePublic')}
                            </Badge>
                          </td>
                          <td className='py-1 px-2'>
                            <Badge variant={isShared ? 'info' : 'default'}>{e.source}</Badge>
                          </td>
                          <td className='py-1 px-2 text-gray-400'>{e.reason ?? '—'}</td>
                          <td className='py-1 px-2 text-gray-500'>
                            {e.expires_at ? new Date(e.expires_at).toLocaleString('cs-CZ') : t('selfDefence.permanent')}
                          </td>
                          <td className='py-1 px-2 text-gray-500'>{e.node_id ?? '—'}</td>
                          <td className='py-1 px-2 text-right'>
                            <div className='flex justify-end gap-1'>
                              {!isShared && (
                                <Button
                                  variant='secondary'
                                  size='sm'
                                  icon={<Globe size={10} />}
                                  loading={publishMut.isPending}
                                  onClick={() => publishMut.mutate({ ip: e.ip, publish: true })}
                                  title={t('selfDefence.publishToMeshTitle')}
                                >
                                  {t('selfDefence.share')}
                                </Button>
                              )}
                              <Button
                                variant='danger'
                                size='sm'
                                icon={<Trash2 size={10} />}
                                loading={removeMut.isPending}
                                onClick={async () => {
                                  if (await themedConfirm(t('selfDefence.confirmRemove', { ip: e.ip, list: listType }))) removeMut.mutate(e.ip);
                                }}
                              >
                                {t('selfDefence.delete')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
      </CardContent>

      {adding && (
        <Modal onClose={() => setAdding(false)} title={t('selfDefence.addToList', { list: listType })} size='sm'>
          <div className='space-y-3'>
            <div>
              <label className='text-xs text-gray-500'>{t('selfDefence.ipOrCidrLabel')}</label>
              <input
                type='text'
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder={t('selfDefence.ipPlaceholder')}
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500'>{t('selfDefence.reasonOptionalLabel')}</label>
              <input
                type='text'
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder={t('selfDefence.reasonPlaceholder')}
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500'>{t('selfDefence.expiresLabel')}</label>
              <input
                type='number'
                value={newExpiresHours}
                onChange={(e) => setNewExpiresHours(e.target.value)}
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
              />
            </div>
            <div className='flex gap-2 justify-end'>
              <Button variant='secondary' onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
              <Button
                variant='primary'
                onClick={() => addMut.mutate()}
                loading={addMut.isPending}
                disabled={!newIp.trim()}
              >
                {t('selfDefence.add')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function SecurityLogPanel() {
  const { t } = useTranslation();
  const [sinceMin, setSinceMin] = useState(60);
  const [severityFilter, setSeverityFilter] = useState<string>('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['self-defence-log', sinceMin, severityFilter],
    queryFn: () =>
      callMcp<{ ok: boolean; events: SecurityEvent[]; total: number }>(
        'self-defence.log_query',
        {
          since_minutes: sinceMin,
          ...(severityFilter ? { severity: severityFilter } : {}),
          limit: 200,
        },
      ),
    refetchInterval: 15_000,
  });
  const events = data?.events ?? [];

  const sevBadge = (s: string) => {
    if (s === 'critical') return <Badge variant='error'>{s}</Badge>;
    if (s === 'high') return <Badge variant='warning'>{s}</Badge>;
    if (s === 'medium') return <Badge variant='info'>{s}</Badge>;
    return <Badge variant='default'>{s}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={AlertTriangle}>{t('selfDefence.securityLogTitle')} ({data?.total ?? 0})</CardTitle>
          <div className='flex gap-2 items-center'>
            <Select
              value={sinceMin}
              onChange={(e) => setSinceMin(parseInt(e.target.value, 10))}
            >
              <option value={15}>{t('selfDefence.optMinutes', { count: 15 })}</option>
              <option value={60}>{t('selfDefence.optHours', { count: 1 })}</option>
              <option value={360}>{t('selfDefence.optHours', { count: 6 })}</option>
              <option value={1440}>{t('selfDefence.optHours', { count: 24 })}</option>
              <option value={10080}>{t('selfDefence.optDays', { count: 7 })}</option>
            </Select>
            <Select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value=''>{t('selfDefence.allSeverities')}</option>
              <option value='critical'>{t('selfDefence.sevCritical')}</option>
              <option value='high'>{t('selfDefence.sevHigh')}</option>
              <option value='medium'>{t('selfDefence.sevMedium')}</option>
              <option value='low'>{t('selfDefence.sevLow')}</option>
            </Select>
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('selfDefence.refresh')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <div className='text-xs text-gray-600 py-4'>{t('common.loading')}</div>
          : events.length === 0
            ? <div className='text-xs text-gray-600 py-4 text-center'>{t('selfDefence.noEventsInWindow')}</div>
            : (
              <div className='overflow-x-auto max-h-96 overflow-y-auto'>
                <table className='w-full text-xs'>
                  <thead className='sticky top-0 bg-black'>
                    <tr className='text-gray-500 border-b border-gray-800'>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colWhen')}</th>
                      <th className='text-left py-1 px-2'>IP</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colSeverity')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colKind')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colSubject')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colModule')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colNode')}</th>
                      <th className='text-left py-1 px-2'>{t('selfDefence.colReasonLog')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.event_uuid} className='border-b border-gray-900 hover:bg-gray-900/40'>
                        <td className='py-1 px-2 text-gray-500'>{new Date(e.ts).toLocaleString('cs-CZ')}</td>
                        <td className='py-1 px-2 font-mono text-white'>{e.source_ip}</td>
                        <td className='py-1 px-2'>{sevBadge(e.severity)}</td>
                        <td className='py-1 px-2 text-gray-400 font-mono'>{e.kind}</td>
                        <td className='py-1 px-2 text-gray-400'>{e.subject ?? '—'}</td>
                        <td className='py-1 px-2 text-gray-500'>{e.source_module}</td>
                        <td className='py-1 px-2 text-gray-500'>{e.source_node}</td>
                        <td className='py-1 px-2 text-gray-400'>{e.reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </CardContent>
    </Card>
  );
}

function TopAttackersPanel() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['self-defence-aggregated'],
    queryFn: () =>
      callMcp<{ ok: boolean; aggregated: AggregatedRow[] }>('self-defence.log_query', {
        since_minutes: 60,
        aggregate_by_ip: true,
        limit: 10,
      }),
    refetchInterval: 30_000,
  });
  const rows = data?.aggregated ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={ShieldAlert}>{t('selfDefence.topAttackersTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <div className='text-xs text-gray-600 py-4'>{t('common.loading')}</div>
          : rows.length === 0
            ? <div className='text-xs text-gray-600 py-4 text-center'>{t('selfDefence.noSuspiciousActivity')}</div>
            : (
              <table className='w-full text-xs'>
                <thead>
                  <tr className='text-gray-500 border-b border-gray-800'>
                    <th className='text-left py-1 px-2'>IP</th>
                    <th className='text-right py-1 px-2'>{t('selfDefence.colAttempts')}</th>
                    <th className='text-left py-1 px-2'>{t('selfDefence.colFirst')}</th>
                    <th className='text-left py-1 px-2'>{t('selfDefence.colLast')}</th>
                    <th className='text-left py-1 px-2'>{t('selfDefence.colKinds')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.source_ip} className='border-b border-gray-900'>
                      <td className='py-1 px-2 font-mono text-white'>{r.source_ip}</td>
                      <td className='py-1 px-2 text-right font-mono text-red-400'>{r.cnt}</td>
                      <td className='py-1 px-2 text-gray-500'>{new Date(r.first_ts).toLocaleString('cs-CZ')}</td>
                      <td className='py-1 px-2 text-gray-500'>{new Date(r.last_ts).toLocaleString('cs-CZ')}</td>
                      <td className='py-1 px-2 text-gray-400 font-mono text-[10px]'>{r.kinds.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </CardContent>
    </Card>
  );
}

function AutoBlockConfigPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['self-defence-status'],
    queryFn: () =>
      callMcp<{ ok: boolean; config: Record<string, unknown> }>('self-defence.utm_status', {}),
    refetchInterval: 60_000,
  });
  const cfg = (data?.config ?? {}) as Record<string, unknown>;

  const updateConfigMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.mcp('self-defence.config', { action: 'set', key, value }),
    onSuccess: () => {
      toast.success(t('selfDefence.settingsSaved'));
      qc.invalidateQueries({ queryKey: ['self-defence-status'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const evaluateMut = useMutation({
    mutationFn: (dryRun: boolean) =>
      callMcp<{ ok: boolean; evaluated: number; blocked: number; actions: unknown[] }>(
        'self-defence.evaluate_blocks',
        { dry_run: dryRun },
      ),
    onSuccess: (r, dryRun) => {
      toast.success(
        dryRun
          ? t('selfDefence.evalResultDry', { evaluated: r.evaluated, blocked: r.blocked })
          : t('selfDefence.evalResult', { evaluated: r.evaluated, blocked: r.blocked }),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Card><CardContent>{t('common.loading')}</CardContent></Card>;

  const ToggleRow = ({ k, label }: { k: string; label: string }) => (
    <div className='flex items-center justify-between py-2 border-b border-gray-800'>
      <span className='text-sm text-white'>{label}</span>
      <input
        type='checkbox'
        checked={Boolean(cfg[k])}
        onChange={(e) => updateConfigMut.mutate({ key: k, value: e.target.checked })}
        className='w-4 h-4'
      />
    </div>
  );

  const NumRow = ({ k, label, suffix }: { k: string; label: string; suffix?: string }) => (
    <div className='flex items-center justify-between py-2 border-b border-gray-800'>
      <span className='text-sm text-white'>{label}</span>
      <div className='flex items-center gap-2'>
        <input
          type='number'
          defaultValue={cfg[k] as number}
          onBlur={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v !== cfg[k]) updateConfigMut.mutate({ key: k, value: v });
          }}
          className='w-20 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-right'
        />
        {suffix && <span className='text-xs text-gray-500'>{suffix}</span>}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={Shield}>{t('selfDefence.autoBlockConfigTitle')}</CardTitle>
          <div className='flex gap-2'>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => evaluateMut.mutate(true)}
              loading={evaluateMut.isPending}
            >
              {t('selfDefence.dryRun')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              onClick={() => evaluateMut.mutate(false)}
              loading={evaluateMut.isPending}
            >
              {t('selfDefence.evaluateNow')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-1'>
          <ToggleRow k='auto_block_enabled' label={t('selfDefence.autoBlockEnabledLabel')} />
          <NumRow k='auto_block_threshold' label={t('selfDefence.thresholdLabel')} />
          <NumRow k='auto_block_window_seconds' label={t('selfDefence.windowLabel')} suffix='s' />
          <NumRow k='auto_block_expires_hours' label={t('selfDefence.blockExpiresLabel')} suffix='h' />
          <ToggleRow k='blacklist_nginx_sync' label={t('selfDefence.syncNginxLabel')} />
          <ToggleRow k='mqtt_replication' label={t('selfDefence.mqttReplicationLabel')} />
          <ToggleRow k='fail2ban_feed' label={t('selfDefence.fail2banFeedLabel')} />
        </div>
        <p className='text-[10px] text-gray-600 mt-3'>
          {t('selfDefence.meshShareHint')}
        </p>
      </CardContent>
    </Card>
  );
}

/** Customer networks (CIDR → zákazník) — source of truth = geo.network_*. */
function CustomerNetworksPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState<'all' | 'private' | 'public'>('all');
  const [cidr, setCidr] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-networks', scope],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        networks: Array<{
          id: string;
          cidr: string;
          label: string | null;
          customer_id: string | null;
          customer_label: string | null;
          is_private: boolean;
          kind: string;
          lat: number | null;
          lon: number | null;
          color: string | null;
        }>;
      }>('geo.network_list', { scope }),
    refetchInterval: 30_000,
  });
  const networks = data?.networks ?? [];

  const setMut = useMutation({
    mutationFn: () =>
      callMcp('geo.network_set', {
        cidr: cidr.trim(),
        customer_label: customerLabel.trim() || undefined,
        customer_id: customerId.trim() || undefined,
        lat: lat ? parseFloat(lat) : undefined,
        lon: lon ? parseFloat(lon) : undefined,
        kind: 'customer',
      }),
    onSuccess: () => {
      toast.success(t('selfDefence.networkSaved'));
      setCidr('');
      setCustomerLabel('');
      setCustomerId('');
      setLat('');
      setLon('');
      qc.invalidateQueries({ queryKey: ['geo-networks'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rmMut = useMutation({
    mutationFn: (id: string) => callMcp('geo.network_delete', { id }),
    onSuccess: () => {
      toast.success(t('selfDefence.networkRemoved'));
      qc.invalidateQueries({ queryKey: ['geo-networks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full flex-wrap gap-2'>
          <CardTitle icon={Globe}>
            {t('selfDefence.networksTitle')} ({networks.length})
          </CardTitle>
          <div className='flex gap-2 items-center'>
            <Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value='all'>{t('selfDefence.scopeAll')}</option>
              <option value='private'>{t('selfDefence.scopePrivate')}</option>
              <option value='public'>{t('selfDefence.scopePublic')}</option>
            </Select>
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('selfDefence.refresh')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-xs text-gray-500'>{t('selfDefence.networksHint')}</p>
        <div className='flex flex-wrap gap-2 items-end'>
          <input
            className='w-36 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            placeholder='10.50.0.0/16'
          />
          <input
            className='w-40 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
            value={customerLabel}
            onChange={(e) => setCustomerLabel(e.target.value)}
            placeholder={t('selfDefence.customerLabelPh')}
          />
          <input
            className='w-36 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder='customer_id'
          />
          <input
            className='w-20 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder='lat'
          />
          <input
            className='w-20 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
            value={lon}
            onChange={(e) => setLon(e.target.value)}
            placeholder='lon'
          />
          <Button
            variant='primary'
            size='sm'
            loading={setMut.isPending}
            disabled={!cidr.trim()}
            onClick={() => setMut.mutate()}
          >
            {t('selfDefence.add')}
          </Button>
        </div>
        {isLoading
          ? <div className='text-xs text-gray-600'>{t('common.loading')}</div>
          : networks.length === 0
          ? <div className='text-xs text-gray-600 text-center py-4'>{t('selfDefence.noNetworks')}</div>
          : (
            <table className='w-full text-xs'>
              <thead>
                <tr className='text-gray-500 border-b border-gray-800'>
                  <th className='text-left py-1 px-2'>CIDR</th>
                  <th className='text-left py-1 px-2'>{t('selfDefence.colScope')}</th>
                  <th className='text-left py-1 px-2'>{t('selfDefence.colCustomer')}</th>
                  <th className='text-left py-1 px-2'>Map</th>
                  <th className='text-right py-1 px-2'>{t('selfDefence.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {networks.map((n) => (
                  <tr key={n.id} className='border-b border-gray-900'>
                    <td className='py-1 px-2 font-mono text-white'>{n.cidr}</td>
                    <td className='py-1 px-2'>
                      <Badge variant={n.is_private ? 'default' : 'info'}>
                        {n.is_private ? t('selfDefence.scopePrivate') : t('selfDefence.scopePublic')}
                      </Badge>
                    </td>
                    <td className='py-1 px-2 text-gray-400'>
                      {n.customer_label || n.customer_id || '—'}
                    </td>
                    <td className='py-1 px-2 font-mono text-gray-500'>
                      {n.lat != null && n.lon != null
                        ? `${Number(n.lat).toFixed(2)},${Number(n.lon).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className='py-1 px-2 text-right'>
                      <Button
                        variant='danger'
                        size='sm'
                        icon={<Trash2 size={10} />}
                        loading={rmMut.isPending}
                        onClick={() => rmMut.mutate(n.id)}
                      >
                        {t('selfDefence.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </CardContent>
    </Card>
  );
}

/** Country block/allow — source of truth = geo.country_policy_* (shared with map). */
function CountryBlocksPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [listType, setListType] = useState<'block' | 'allow'>('block');
  const [reason, setReason] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-countries'],
    queryFn: () =>
      callMcp<{ ok: boolean; policies: Array<{ country_code: string; list_type: string; reason: string | null }> }>(
        'geo.country_policy_list',
        {},
      ),
    refetchInterval: 30_000,
  });
  const policies = data?.policies ?? [];

  const setMut = useMutation({
    mutationFn: () =>
      callMcp('geo.country_policy_set', {
        country_code: code.trim().toUpperCase(),
        list_type: listType,
        reason: reason.trim() || undefined,
        mesh_share: true,
      }),
    onSuccess: () => {
      toast.success(t('selfDefence.countrySaved'));
      setCode('');
      setReason('');
      qc.invalidateQueries({ queryKey: ['geo-countries'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rmMut = useMutation({
    mutationFn: (cc: string) =>
      callMcp('geo.country_policy_remove', { country_code: cc, mesh_unshare: true }),
    onSuccess: () => {
      toast.success(t('selfDefence.countryRemoved'));
      qc.invalidateQueries({ queryKey: ['geo-countries'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={Globe}>
            {t('selfDefence.countriesTitle')} ({policies.length})
          </CardTitle>
          <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
            {t('selfDefence.refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-xs text-gray-500'>{t('selfDefence.countriesHint')}</p>
        <div className='grid grid-cols-1 sm:grid-cols-12 gap-3 items-end'>
          <div className='sm:col-span-2'>
            <Label htmlFor='sd-cc-iso'>ISO</Label>
            <Input
              id='sd-cc-iso'
              size='sm'
              className='font-mono uppercase text-center tracking-widest'
              maxLength={2}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder='RU'
              autoComplete='off'
            />
          </div>
          <div className='sm:col-span-3'>
            <Label htmlFor='sd-cc-policy'>{t('selfDefence.colPolicy', 'Politika')}</Label>
            <Select
              id='sd-cc-policy'
              size='sm'
              className='w-full'
              value={listType}
              onChange={(e) => setListType(e.target.value as 'block' | 'allow')}
              options={[
                { value: 'block', label: 'block' },
                { value: 'allow', label: 'allow' },
              ]}
            />
          </div>
          <div className='sm:col-span-5'>
            <Label htmlFor='sd-cc-reason'>{t('selfDefence.colReason')}</Label>
            <Input
              id='sd-cc-reason'
              size='sm'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('selfDefence.reasonPlaceholder')}
            />
          </div>
          <div className='sm:col-span-2'>
            <Button
              variant='primary'
              size='sm'
              className='w-full h-8'
              loading={setMut.isPending}
              disabled={!/^[A-Za-z]{2}$/.test(code.trim())}
              onClick={() => setMut.mutate()}
            >
              {t('selfDefence.add')}
            </Button>
          </div>
        </div>
        {isLoading
          ? <div className='text-xs text-gray-600'>{t('common.loading')}</div>
          : policies.length === 0
          ? <div className='text-xs text-gray-600 text-center py-4'>{t('selfDefence.noCountries')}</div>
          : (
            <table className='w-full text-xs'>
              <thead>
                <tr className='text-gray-500 border-b border-gray-800'>
                  <th className='text-left py-1 px-2'>CC</th>
                  <th className='text-left py-1 px-2'>{t('selfDefence.colSource')}</th>
                  <th className='text-left py-1 px-2'>{t('selfDefence.colReason')}</th>
                  <th className='text-right py-1 px-2'>{t('selfDefence.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.country_code} className='border-b border-gray-900'>
                    <td className='py-1 px-2 font-mono text-white'>{p.country_code}</td>
                    <td className='py-1 px-2'>
                      <Badge variant={p.list_type === 'block' ? 'error' : 'info'}>{p.list_type}</Badge>
                    </td>
                    <td className='py-1 px-2 text-gray-400'>{p.reason ?? '—'}</td>
                    <td className='py-1 px-2 text-right'>
                      <Button
                        variant='danger'
                        size='sm'
                        icon={<Trash2 size={10} />}
                        loading={rmMut.isPending}
                        onClick={() => rmMut.mutate(p.country_code)}
                      >
                        {t('selfDefence.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

// ── Reaction: fail2ban most jako jeden přepínač ─────────────────────────────
// Stav = self-defence config `fail2ban_feed`; set skrz self-defence.config most
// skutečně nainstaluje/odebere. Po publikaci veřejného přístupu ho platforma zapne sama.
function ReactionPanel() {
  const { setActiveTab } = useTabs();
  return <Fail2banReactionCard onOpenList={(list) => setActiveTab(list)} />;
}

export default function SelfDefencePage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') ?? 'overview';
  const { t } = useTranslation();
  const { data: status } = useQuery({
    queryKey: ['self-defence-status-stats'],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        config: Record<string, unknown>;
        summary: { blacklist: number; whitelist: number; sources: string[]; expiring_soon: number };
      }>('self-defence.utm_status', {}),
    refetchInterval: 30_000,
  });

  const { data: events24h } = useQuery({
    queryKey: ['self-defence-events-24h'],
    queryFn: () =>
      callMcp<{ ok: boolean; total: number }>('self-defence.log_query', {
        since_minutes: 1440,
        limit: 1,
      }),
    refetchInterval: 60_000,
  });

  const blacklist = status?.summary.blacklist ?? 0;
  const whitelist = status?.summary.whitelist ?? 0;
  const events24 = events24h?.total ?? 0;

  return (
    <div className='space-y-4'>
      <StatsGrid columns={3}>
        <StatCard
          label={t('selfDefence.statBlacklistLocal')}
          value={blacklist}
          icon={Ban}
          variant={blacklist > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label={t('selfDefence.whitelist')}
          value={whitelist}
          icon={ShieldCheck}
          variant='info'
        />
        <StatCard
          label={t('selfDefence.statEvents24h')}
          value={events24}
          icon={AlertTriangle}
          variant={events24 > 100 ? 'warning' : 'default'}
        />
      </StatsGrid>

      <Tabs defaultTab={initialTab}>
        <TabList>
          <Tab id='overview' icon={<Shield size={14} />}>{t('selfDefence.tabOverview')}</Tab>
          <Tab id='map' icon={<MapIcon size={14} />}>{t('selfDefence.tabMap', 'Mapa')}</Tab>
          <Tab id='blacklist' icon={<Ban size={14} />}>{t('selfDefence.blacklist')}</Tab>
          <Tab id='whitelist' icon={<ShieldCheck size={14} />}>{t('selfDefence.whitelist')}</Tab>
          <Tab id='countries' icon={<Globe size={14} />}>{t('selfDefence.tabCountries')}</Tab>
          <Tab id='networks' icon={<Globe size={14} />}>{t('selfDefence.tabNetworks')}</Tab>
          <Tab id='log' icon={<AlertTriangle size={14} />}>{t('selfDefence.tabLog')}</Tab>
          <Tab id='config' icon={<CheckCircle2 size={14} />}>{t('selfDefence.tabConfig')}</Tab>
        </TabList>

        <TabPanel id='overview'>
          <div className='space-y-4'>
            <ReactionPanel />
            <Card>
              <CardHeader>
                <CardTitle icon={MapIcon}>
                  {t('selfDefence.mapTitle', 'Mapa útoků (nody + zdroje)')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-xs text-gray-500 mb-2'>
                  {t(
                    'selfDefence.mapHint',
                    'MCP geo.map_snapshot (include_attacks). Červené linky = attacker IP → cílový node. Geocode pending public IPs v modulu Geo.',
                  )}
                </p>
                <GeoMap
                  format='md'
                  preset='defence'
                  defaultShowAttacks
                  includeCallers={false}
                  showControls
                />
              </CardContent>
            </Card>
            <TopAttackersPanel />
            <SecurityLogPanel />
          </div>
        </TabPanel>

        <TabPanel id='map'>
          <Card>
            <CardContent className='pt-4'>
              <GeoMap
                format='lg'
                preset='defence'
                defaultShowAttacks
                includeCallers={false}
                showControls
              />
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='blacklist'>
          <IpListPanel listType='blacklist' />
        </TabPanel>

        <TabPanel id='whitelist'>
          <IpListPanel listType='whitelist' />
        </TabPanel>

        <TabPanel id='countries'>
          <CountryBlocksPanel />
        </TabPanel>

        <TabPanel id='networks'>
          <CustomerNetworksPanel />
        </TabPanel>

        <TabPanel id='log'>
          <SecurityLogPanel />
        </TabPanel>

        <TabPanel id='config'>
          <AutoBlockConfigPanel />
        </TabPanel>
      </Tabs>
    </div>
  );
}
